import React from 'react';

import {
  getEntityDiagnosticsAttributesFloatSeriesCountLimitExceeded,
  getEntityDiagnosticsAttributesTotalCountLimitExceeded,
  getEntityDiagnosticsDroppedFloatSeriesCount,
  getEntityFailed,
  getEntityState,
} from '@neptune/shared/entity-domain';

import { Attribute } from 'domain/experiment/attribute';
import { isStateRunning } from 'domain/experiment/experiment-state';

const FLOAT_SERIES_LIMIT_WARNING_TEXT =
  'To maintain system stability, Neptune caps each run at 1M float-series attributes. This run has reached that limit, so existing float-series attributes can still update, but new float-series attributes may be rejected. Please review your attribute naming to reduce cardinality.';
const TOTAL_ATTRIBUTE_LIMIT_WARNING_TEXT =
  'To maintain system stability, Neptune caps each run at 1M attributes. This run has reached that limit, so existing attributes can still update, but new attributes may be rejected.';

function getDiagnostics(state: any) {
  const isRunning = state.running;

  if (state.failed) {
    return {
      isRunning: false,
      state: 'failed',
    } as const;
  }

  if (state.totalAttributeCountLimitExceeded) {
    return {
      state: 'warning',
      text: TOTAL_ATTRIBUTE_LIMIT_WARNING_TEXT,
      isRunning,
    } as const;
  }

  if (state.floatSeriesCountLimitExceeded || state.droppedFloatSeriesCount > 0) {
    return {
      state: 'warning',
      text: FLOAT_SERIES_LIMIT_WARNING_TEXT,
      isRunning,
    } as const;
  }

  if (isRunning) {
    return {
      isRunning,
      state: 'running',
    } as const;
  }

  return undefined;
}

export function useEntityDiagnostics(data?: { attributes: Attribute[] }) {
  return React.useMemo(() => {
    if (!data) {
      return undefined;
    }

    const metadata = {
      failed: getEntityFailed(data),
      running: isStateRunning(getEntityState(data)),
      totalAttributeCountLimitExceeded: getEntityDiagnosticsAttributesTotalCountLimitExceeded(data),
      floatSeriesCountLimitExceeded:
        getEntityDiagnosticsAttributesFloatSeriesCountLimitExceeded(data),
      droppedFloatSeriesCount: getEntityDiagnosticsDroppedFloatSeriesCount(data),
    };

    return getDiagnostics(metadata);
  }, [data]);
}
